import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import AdminLayout from "../components/AdminLayout";
import WorkerLayout from "../components/WorkerLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Bell, Check, CheckCheck, LogIn, LogOut, Clock, AlertCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAdmin } = useAuth();

  const Layout = isAdmin ? AdminLayout : WorkerLayout;

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      toast.error("Erro ao carregar notificações");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      // Remove from list since it's deleted on backend
      setNotifications(notifications.filter(n => n.id !== id));
      toast.success("Notificação removida");
    } catch (error) {
      toast.error("Erro ao remover notificação");
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications([]);
      toast.success("Todas notificações removidas");
    } catch (error) {
      toast.error("Erro ao remover notificações");
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "entry":
        return <LogIn className="w-4 h-4 text-[#2ECC40]" />;
      case "exit":
        return <LogOut className="w-4 h-4 text-[#F5A623]" />;
      case "manual":
        return <Clock className="w-4 h-4 text-blue-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Agora";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m atrás`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h atrás`;
    
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const unreadCount = notifications.length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 font-manrope">Notificações</h1>
            <p className="text-gray-500 mt-1">
              {unreadCount > 0 ? `${unreadCount} não lidas` : "Todas lidas"}
            </p>
          </div>
          
          {notifications.length > 0 && (
            <Button
              variant="outline"
              className="border-gray-200 text-gray-600 hover:bg-gray-50"
              onClick={markAllAsRead}
              data-testid="mark-all-read-btn"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Limpar todas
            </Button>
          )}
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardContent className="py-16">
              <div className="empty-state">
                <Bell className="empty-state-icon" />
                <p className="empty-state-title">Nenhuma notificação</p>
                <p className="empty-state-description">
                  Você receberá notificações sobre registros de ponto aqui
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <Card 
                key={notification.id} 
                className={`bg-white border-gray-100 shadow-sm card-hover ${
                  !notification.read ? "border-l-4 border-l-[#F5A623]" : ""
                }`}
                data-testid={`notification-${notification.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      notification.type === "entry" ? "bg-green-100" :
                      notification.type === "exit" ? "bg-amber-100" :
                      notification.type === "manual" ? "bg-blue-100" :
                      "bg-gray-100"
                    }`}>
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className={`font-medium ${notification.read ? "text-gray-500" : "text-gray-800"}`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {notification.message}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markAsRead(notification.id)}
                          className="text-gray-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0"
                          data-testid={`delete-notification-${notification.id}`}
                          title="Remover notificação"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      <p className="text-xs text-gray-400 mt-2 font-mono">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
